import { useEffect, useRef, useState } from "react";
import { Form, required, useLogin, useNotify, useTranslate } from "ra-core";
import type { SubmitHandler, FieldValues } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";
import { Notification } from "@/components/admin/notification";
import { SSOAuthButton } from "./SSOAuthButton";
import {
  disableEmailPasswordAuthentication,
  googleWorkplaceDomain,
  microsoftEnabled,
} from "./authConfig";
// GAVION: Gavion login layout, Microsoft sign-in and error mapping
import { LoginHero } from "@/components/gavion/login/LoginHero";
import { LocaleToggle } from "@/components/gavion/login/LocaleToggle";
import { MicrosoftSignInButton } from "@/components/gavion/login/MicrosoftSignInButton";
import {
  mapLoginError,
  parseAuthReturnError,
} from "@/components/gavion/login/loginErrors";

/**
 * Login page displayed when authentication is enabled and the user is not authenticated.
 *
 * Automatically shown when an unauthenticated user tries to access a protected route.
 * Handles login via authProvider.login() and displays error notifications on failure.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/loginpage LoginPage documentation}
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/security Security documentation}
 */
export const LoginPage = (props: { redirectTo?: string }) => {
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  // GAVION: inline login error key
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const hasDisplayedRecoveryNotification = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const notify = useNotify();
  const translate = useTranslate();

  // GAVION: show OAuth errors returned in the URL, then clean it
  useEffect(() => {
    const key = parseAuthReturnError(window.location);
    if (!key) return;
    setErrorKey(key);
    const url = new URL(window.location.href);
    url.search = "";
    const route = url.hash.startsWith("#/") ? url.hash.split("?")[0] : "";
    window.history.replaceState(null, "", url.pathname + route);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldNotify = searchParams.get("passwordRecoveryEmailSent") === "1";

    if (!shouldNotify || hasDisplayedRecoveryNotification.current) {
      return;
    }

    hasDisplayedRecoveryNotification.current = true;
    notify("crm.auth.recovery_email_sent", {
      type: "success",
      messageArgs: {
        _: "If you're a registered user, you should receive a password recovery email shortly.",
      },
    });

    searchParams.delete("passwordRecoveryEmailSent");
    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, notify]);

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    setErrorKey(null);
    login(values, redirectTo)
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        // GAVION: map errors to Gavion copy instead of raw notifications
        setErrorKey(mapLoginError(error));
      });
  };

  return (
    <div className="min-h-screen">
      {/* GAVION: split layout, hero left and card right */}
      <div className="relative grid min-h-screen md:grid-cols-2">
        <LoginHero />
        <div className="relative flex flex-col justify-center w-full p-4 lg:p-8">
          <div className="absolute right-4 top-4">
            <LocaleToggle />
          </div>
          <div className="w-full space-y-6 lg:mx-auto lg:w-[350px]">
            <div className="text-center">
              <h1 className="text-2xl font-semibold tracking-tight">
                {translate("gavion.login.title")}
              </h1>
            </div>
            {errorKey ? (
              <p role="alert" className="text-sm text-destructive text-center">
                {translate(`gavion.login.errors.${errorKey}`)}
                {errorKey === "network" ? (
                  <button
                    type="button"
                    className="ml-2 underline"
                    onClick={() => setErrorKey(null)}
                  >
                    {translate("gavion.login.dismiss")}
                  </button>
                ) : null}
              </p>
            ) : null}
            {microsoftEnabled ? (
              <>
                <MicrosoftSignInButton onError={setErrorKey} />
                {disableEmailPasswordAuthentication ? null : (
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <div className="h-px flex-1 bg-border" />
                    {translate("gavion.login.or")}
                    <div className="h-px flex-1 bg-border" />
                  </div>
                )}
              </>
            ) : null}
            {disableEmailPasswordAuthentication ? null : (
              <Form className="space-y-8" onSubmit={handleSubmit}>
                <TextInput
                  label="ra.auth.email"
                  source="email"
                  type="email"
                  validate={required()}
                  disabled={loading}
                />
                <TextInput
                  label="ra.auth.password"
                  source="password"
                  type="password"
                  validate={required()}
                  disabled={loading}
                />
                <div className="flex flex-col gap-4">
                  <Button
                    type="submit"
                    className="cursor-pointer"
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="animate-spin" /> : null}
                    {translate("gavion.login.submit")}
                  </Button>
                </div>
              </Form>
            )}
            {googleWorkplaceDomain ? (
              <SSOAuthButton className="w-full" domain={googleWorkplaceDomain}>
                {translate("crm.auth.sign_in_google_workspace", {
                  _: "Sign in with Google Workplace",
                })}
              </SSOAuthButton>
            ) : null}
            {disableEmailPasswordAuthentication ? null : (
              <Link
                to={"/forgot-password"}
                className="block text-sm text-center hover:underline"
              >
                {translate("gavion.login.forgot")}
              </Link>
            )}
          </div>
        </div>
      </div>
      <Notification />
    </div>
  );
};
